import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import type { ActivityPrompt } from '@qq/shared';

/**
 * The writing prompts: tracing, ordering, and a child's own writing.
 *
 * The tracing surface reports the raw finger path and nothing else. It makes no
 * judgement of its own - the server compares the path against the letterform -
 * so what the child is told about their writing is always something the app
 * actually measured.
 */

export type Point = { x: number; y: number };

/** In letter units (the box is 1 wide): a twentieth of the box is a stroke. */
const MIN_STROKE = 0.05;

function strokeLength(points: Point[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1]!;
    const b = points[i]!;
    total += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return total;
}

interface TraceProps {
  prompt: Extract<ActivityPrompt, { kind: 'traceLetter' }>;
  onSubmit: (choiceId: string, traced: Point[][]) => void;
  disabled: boolean;
}

export function LetterTrail({ prompt, onSubmit, disabled }: TraceProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [strokeIndex, setStrokeIndex] = useState(0);
  const [drawn, setDrawn] = useState<Point[][]>([]);
  const [current, setCurrent] = useState<Point[]>([]);
  const drawing = useRef(false);

  const expected = prompt.strokes[strokeIndex];
  const finished = strokeIndex >= prompt.strokes.length;

  /** Screen coordinates into the 0..1 space the letterforms are drawn in. */
  const toLocal = (event: ReactPointerEvent): Point | null => {
    const svg = svgRef.current;
    if (!svg) return null;
    const box = svg.getBoundingClientRect();
    return {
      x: (event.clientX - box.left) / box.width,
      y: (event.clientY - box.top) / box.height,
    };
  };

  const begin = (event: ReactPointerEvent) => {
    if (disabled) return;
    const point = toLocal(event);
    if (!point) return;
    // Touching the letter again once every stroke is down starts it over: a
    // child who cannot read "Start again" must still be able to have another go.
    if (finished) {
      setDrawn([]);
      setStrokeIndex(0);
    }
    drawing.current = true;
    setCurrent([point]);
    // Captured on the SURFACE, not whichever line or path the finger landed
    // on, so the stroke keeps following the finger if it strays outside.
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const move = (event: ReactPointerEvent) => {
    if (!drawing.current) return;
    const point = toLocal(event);
    if (point) setCurrent((path) => [...path, point]);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const stroke = current;
    setCurrent([]);
    // A brush of the finger is not a stroke. Counting it used to finish a
    // one-stroke letter before the child had started.
    if (strokeLength(stroke) < MIN_STROKE) return;
    setDrawn((all) => [...all, stroke]);
    setStrokeIndex((i) => i + 1);
  };

  const reset = () => {
    setDrawn([]);
    setCurrent([]);
    setStrokeIndex(0);
  };

  const path = (points: Point[]) =>
    points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x * 200} ${p.y * 200}`).join(' ');

  return (
    <>
      <h1>{prompt.instruction}</h1>

      <svg
        ref={svgRef}
        viewBox="0 0 200 200"
        className="trace-surface"
        onPointerDown={begin}
        onPointerMove={move}
        onPointerUp={end}
        // Not onPointerLeave: a finger drifting past the edge mid-letter used
        // to end the stroke there, and on a one-stroke letter that was the end.
        onPointerCancel={end}
        role="img"
        aria-label={`Trace the letter ${prompt.character}`}
      >
        {/* Writing lines, the way a school exercise book has them. */}
        <line x1="0" y1="70" x2="200" y2="70" stroke="#ddd5c6" strokeWidth="1.5" />
        <line x1="0" y1="150" x2="200" y2="150" stroke="#c9c0ae" strokeWidth="2.5" />

        {/* The guide. It fades out at the harder levels rather than vanishing
            entirely, so the child always knows what shape they are aiming at. */}
        {prompt.strokes.map((stroke, i) => (
          <path
            key={i}
            d={path(stroke.points)}
            fill="none"
            stroke="#c9c0ae"
            strokeWidth={prompt.level === 'guided' ? 16 : prompt.level === 'faded' ? 10 : 4}
            strokeOpacity={prompt.level === 'free' ? 0.25 : 0.55}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}

        {/* Where to start, and which way to go. */}
        {expected && (
          <>
            <circle
              cx={(expected.points[0]?.x ?? 0) * 200}
              cy={(expected.points[0]?.y ?? 0) * 200}
              r="9"
              fill="#f2b134"
              stroke="#2a2e2d"
              strokeWidth="2"
            />
            <text
              x={(expected.points[0]?.x ?? 0) * 200}
              y={(expected.points[0]?.y ?? 0) * 200 + 4}
              textAnchor="middle"
              fontSize="11"
              fontWeight="700"
            >
              {strokeIndex + 1}
            </text>
          </>
        )}

        {drawn.map((stroke, i) => (
          <path
            key={`drawn-${i}`}
            d={path(stroke)}
            fill="none"
            stroke="#2f7d6b"
            strokeWidth="7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
        {current.length > 1 && (
          <path
            d={path(current)}
            fill="none"
            stroke="#2f7d6b"
            strokeWidth="7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>

      <p className="muted">
        {finished
          ? 'All done - check it!'
          : `${expected?.hint ?? ''} (${strokeIndex + 1} of ${prompt.strokes.length})`}
      </p>

      <div className="selfie-actions">
        <button type="button" className="btn btn--outline" onClick={reset} disabled={disabled}>
          Start again
        </button>
        <button
          type="button"
          className="btn"
          disabled={disabled || drawn.length === 0}
          onClick={() => onSubmit('traced', drawn)}
        >
          Check my writing
        </button>
      </div>
    </>
  );
}

interface OrderProps {
  prompt: Extract<ActivityPrompt, { kind: 'orderItems' }>;
  onSubmit: (choiceId: string) => void;
  disabled: boolean;
}

/** Tap-to-place rather than drag: far more reliable for small fingers. */
export function OrderItems({ prompt, onSubmit, disabled }: OrderProps) {
  const [placed, setPlaced] = useState<{ id: string; label: string }[]>([]);
  const remaining = prompt.items.filter((item) => !placed.some((p) => p.id === item.id));

  return (
    <>
      <h1>{prompt.instruction}</h1>

      <div className="order-line" aria-label="Your answer so far">
        {placed.length === 0 && <span className="muted">Tap them in order</span>}
        {placed.map((item) => (
          <button
            key={item.id}
            type="button"
            className={prompt.pictures ? 'order-picture' : 'order-word'}
            onClick={() => setPlaced(placed.filter((p) => p.id !== item.id))}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="order-pool">
        {remaining.map((item) => (
          <button
            key={item.id}
            type="button"
            className={prompt.pictures ? 'order-picture' : 'order-word'}
            disabled={disabled}
            onClick={() => setPlaced([...placed, item])}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="selfie-actions">
        <button type="button" className="btn btn--outline" onClick={() => setPlaced([])}>
          Start again
        </button>
        <button
          type="button"
          className="btn"
          disabled={disabled || remaining.length > 0}
          onClick={() => onSubmit(placed.map((p) => p.label).join(prompt.pictures ? '' : ' '))}
        >
          That&rsquo;s my answer
        </button>
      </div>
    </>
  );
}

interface FreeWritingProps {
  prompt: Extract<ActivityPrompt, { kind: 'freeWriting' }>;
  onSubmit: (choiceId: string, writing: { text?: string; drawing?: string }) => void;
  disabled: boolean;
}

export function FreeWritingCanvas({ prompt, onSubmit, disabled }: FreeWritingProps) {
  const [text, setText] = useState('');
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);

  const draw = (event: ReactPointerEvent<HTMLCanvasElement>, start: boolean) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const box = canvas.getBoundingClientRect();
    const x = ((event.clientX - box.left) / box.width) * canvas.width;
    const y = ((event.clientY - box.top) / box.height) * canvas.height;

    if (start) {
      drawing.current = true;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineWidth = 4;
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#2a2e2d';
      return;
    }
    if (!drawing.current) return;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  return (
    <>
      <h1>{prompt.promptText}</h1>
      {/* Say plainly that the app is not marking this. */}
      <p className="gentle-note">{prompt.instruction}</p>

      <textarea
        className="writing-area"
        value={text}
        disabled={disabled}
        placeholder="Write here…"
        onChange={(e) => setText(e.target.value)}
      />

      {/* The editing pass. Nothing here is checked by the app and ticking none
          of them still submits: the app cannot see whether a capital letter is
          really there, and a tick box that pretended to would be the same lie
          this whole screen exists to avoid. It is a prompt to look again. */}
      {prompt.checklist.length > 0 && text.trim().length > 0 && (
        <div className="edit-pass">
          <h2>Now read it back</h2>
          <ul>
            {prompt.checklist.map((step) => (
              <li key={step}>
                <label>
                  <input type="checkbox" disabled={disabled} /> {step}
                </label>
              </li>
            ))}
          </ul>
          <p className="muted">Nobody checks these. They&rsquo;re just to help you spot things.</p>
        </div>
      )}

      {prompt.allowDrawing && (
        <>
          <canvas
            ref={canvasRef}
            width={640}
            height={360}
            className="writing-canvas"
            onPointerDown={(e) => draw(e, true)}
            onPointerMove={(e) => draw(e, false)}
            onPointerUp={() => (drawing.current = false)}
            onPointerLeave={() => (drawing.current = false)}
          />
          <button
            type="button"
            className="btn btn--quiet"
            onClick={() => {
              const ctx = canvasRef.current?.getContext('2d');
              if (ctx && canvasRef.current) {
                ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
              }
            }}
          >
            Clear the picture
          </button>
        </>
      )}

      <button
        type="button"
        className="btn"
        disabled={disabled || (text.trim().length === 0 && !prompt.allowDrawing)}
        onClick={() =>
          onSubmit('submitted', {
            ...(text.trim() ? { text: text.trim() } : {}),
            ...(prompt.allowDrawing && canvasRef.current
              ? { drawing: canvasRef.current.toDataURL('image/png') }
              : {}),
          })
        }
      >
        Give it to a grown-up
      </button>
    </>
  );
}
